import { useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { NONCE_URL, VERIFY_SIGNATURE_URL } from '../api';

// Create axios instance with consistent configuration
const verifyAxios = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export const useSignatureVerification = () => {
  const { address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { isAuthenticated } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Get a nonce/challenge from the server
  const getNonce = async () => {
    if (!address) {
      setError('Wallet not connected');
      return null;
    }

    if (!isAuthenticated) {
      setError('You must be logged in with Discord first');
      return null;
    }

    try {
      console.log('Requesting nonce from server...');
      const response = await verifyAxios.get(`${NONCE_URL}?address=${address}`);

      console.log('Nonce response:', response.data);
      const nonceMessage = response.data.message;
      setMessage(nonceMessage);
      return nonceMessage;
    } catch (err) {
      console.error('Failed to get nonce:', err);
      console.log('Server error data:', err.response?.data);
      console.log('Server error status:', err.response?.status);
      
      setError('Failed to get verification challenge. Please try again.');
      return null;
    }
  };

  // Sign the message and verify signature
  const verifySignature = async () => {
    if (!address) {
      setError('Wallet not connected');
      return false;
    }

    if (!isAuthenticated) {
      setError('You must be logged in with Discord first');
      return false;
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Get a challenge message if we don't have one
      const messageToSign = message || await getNonce();
      if (!messageToSign) {
        setIsVerifying(false);
        return false;
      }

      console.log('Asking user to sign message:', messageToSign);

      // Ask user to sign the message
      const signature = await signMessageAsync({ 
        message: messageToSign
      });

      console.log('Signature received:', signature);

      // Verify the signature on the server
      const response = await verifyAxios.post(
        VERIFY_SIGNATURE_URL,
        {
          signature,
          message: messageToSign,
          walletAddress: address,
        }
      );

      console.log('Verification response:', response.data);

      if (response.data.verified) {
        setIsVerified(true);
        setIsVerifying(false);
        return true;
      } else {
        setError(response.data.message || 'Signature verification failed');
        setIsVerifying(false);
        return false;
      }
    } catch (err) {
      console.error('Error verifying signature:', err);
      setError(err.response?.data?.message || 'Error verifying signature');
      setIsVerifying(false);
      return false;
    }
  };

  return {
    verifySignature,
    isVerifying,
    isVerified,
    error,
    getNonce
  };
}; 